import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * UI Slice - Manages UI state like sidebar visibility, zoom level, etc.
 */
interface UIState {
  // Sidebar visibility
  showChat: boolean;
  showParticipants: boolean;
  showPolls: boolean;
  showSettings: boolean;
  showNotes: boolean;
  showWhiteboard: boolean;
  showMeetingDetails: boolean;
  showLeaveModal: boolean;

  // Zoom state
  zoomLevel: number;

  // Pinned track
  pinnedTrackSid: string | null;

  // Sound effects preference
  soundEnabled: boolean;
}

const initialState: UIState = {
  showChat: false,
  showParticipants: false,
  showPolls: false,
  showSettings: false,
  showNotes: false,
  showWhiteboard: false,
  showMeetingDetails: false,
  showLeaveModal: false,
  zoomLevel: 1,
  pinnedTrackSid: null,
  soundEnabled: true
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    setShowChat(state, action: PayloadAction<boolean>) {
      state.showChat = action.payload;
    },
    setShowParticipants(state, action: PayloadAction<boolean>) {
      state.showParticipants = action.payload;
    },
    setShowPolls(state, action: PayloadAction<boolean>) {
      state.showPolls = action.payload;
    },
    setShowSettings(state, action: PayloadAction<boolean>) {
      state.showSettings = action.payload;
    },
    setShowNotes(state, action: PayloadAction<boolean>) {
      state.showNotes = action.payload;
    },
    setShowWhiteboard(state, action: PayloadAction<boolean>) {
      state.showWhiteboard = action.payload;
    },
    setShowMeetingDetails(state, action: PayloadAction<boolean>) {
      state.showMeetingDetails = action.payload;
    },
    setShowLeaveModal(state, action: PayloadAction<boolean>) {
      state.showLeaveModal = action.payload;
    },
    setZoomLevel(state, action: PayloadAction<number>) {
      state.zoomLevel = action.payload;
    },
    zoomIn(state) {
      state.zoomLevel = Math.min(state.zoomLevel + 0.25, 2);
    },
    zoomOut(state) {
      state.zoomLevel = Math.max(state.zoomLevel - 0.25, 0.5);
    },
    resetZoom(state) {
      state.zoomLevel = 1;
    },
    setPinnedTrackSid(state, action: PayloadAction<string | null>) {
      state.pinnedTrackSid = action.payload;
    },
    togglePin(state, action: PayloadAction<string>) {
      state.pinnedTrackSid = state.pinnedTrackSid === action.payload ? null : action.payload;
    },
    setSoundEnabled(state, action: PayloadAction<boolean>) {
      state.soundEnabled = action.payload;
    },
    toggleSoundEnabled(state) {
      state.soundEnabled = !state.soundEnabled;
    },
    resetUI() {
      return initialState;
    }
  }
});

export const {
  setShowChat,
  setShowParticipants,
  setShowPolls,
  setShowSettings,
  setShowNotes,
  setShowWhiteboard,
  setShowMeetingDetails,
  setShowLeaveModal,
  setZoomLevel,
  zoomIn,
  zoomOut,
  resetZoom,
  setPinnedTrackSid,
  togglePin,
  setSoundEnabled,
  toggleSoundEnabled,
  resetUI
} = uiSlice.actions;

// Selectors
export const selectShowChat = (state: { ui: UIState }) => state.ui.showChat;
export const selectShowParticipants = (state: { ui: UIState }) => state.ui.showParticipants;
export const selectShowPolls = (state: { ui: UIState }) => state.ui.showPolls;
export const selectShowSettings = (state: { ui: UIState }) => state.ui.showSettings;
export const selectShowNotes = (state: { ui: UIState }) => state.ui.showNotes;
export const selectShowWhiteboard = (state: { ui: UIState }) => state.ui.showWhiteboard;
export const selectShowMeetingDetails = (state: { ui: UIState }) => state.ui.showMeetingDetails;
export const selectShowLeaveModal = (state: { ui: UIState }) => state.ui.showLeaveModal;
export const selectZoomLevel = (state: { ui: UIState }) => state.ui.zoomLevel;
export const selectPinnedTrackSid = (state: { ui: UIState }) => state.ui.pinnedTrackSid;
export const selectSoundEnabled = (state: { ui: UIState }) => state.ui.soundEnabled;

export const selectIsSidebarOpen = (state: { ui: UIState }) =>
  state.ui.showChat ||
  state.ui.showParticipants ||
  state.ui.showPolls ||
  state.ui.showSettings ||
  state.ui.showNotes ||
  state.ui.showLeaveModal;

export default uiSlice.reducer;
